import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { MANAGEMENT_URL } from "@/config/env";

// Token-Refresh-Hook.
// Triggert `POST /auth/refresh` 30s vor `expiresAt`.
// Bei Fehler: bis 3 Retries mit Backoff 1s/2s/5s. Wenn alle fehlschlagen:
// Logout + Toast "Session expired, please login again".

const REFRESH_LEAD_MS = 30 * 1000;
const RETRY_DELAYS_MS = [1000, 2000, 5000];

interface RefreshResponse {
  accessToken: string;
  expiresAt: string; // ISO
}

function isValidRefreshResponse(value: unknown): value is RefreshResponse {
  if (typeof value !== "object" || value === null) return false;
  const r = value as Record<string, unknown>;
  if (typeof r.accessToken !== "string" || r.accessToken.length === 0) return false;
  if (typeof r.expiresAt !== "string") return false;
  // expiresAt muss ISO-parsebar sein
  const ms = new Date(r.expiresAt).getTime();
  return Number.isFinite(ms);
}

async function refreshOnce(token: string): Promise<RefreshResponse> {
  const res = await fetch(`${MANAGEMENT_URL}/auth/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`refresh failed: ${res.status}`);
  }
  const body: unknown = await res.json();
  if (!isValidRefreshResponse(body)) {
    throw new Error("refresh response schema invalid");
  }
  return body;
}

export function useTokenRefresh(): void {
  const { session, setSession, logout } = useAuth();
  // Memo-ref damit re-renders nicht den Timer multiplizieren
  const lastSetForExpiryRef = useRef<number | null>(null);

  useEffect(() => {
    if (!session) {
      lastSetForExpiryRef.current = null;
      return;
    }

    // Identische expiresAt → nichts neu planen
    if (lastSetForExpiryRef.current === session.expiresAt) {
      return;
    }
    lastSetForExpiryRef.current = session.expiresAt;

    // Edge-Case: Token schon abgelaufen (Server-Clock-Skew oder Hydration einer
    // gerade abgelaufenen Session). Sofort logout + Toast — kein Refresh-Loop.
    if (session.expiresAt <= Date.now()) {
      toast.error("Session expired, please login again");
      logout();
      return;
    }

    const msUntilRefresh = Math.max(0, session.expiresAt - Date.now() - REFRESH_LEAD_MS);
    let cancelled = false;

    const timer = setTimeout(async () => {
      if (cancelled) return;

      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        try {
          const fresh = await refreshOnce(session.accessToken);
          if (cancelled) return;
          setSession({
            accessToken: fresh.accessToken,
            expiresAt: new Date(fresh.expiresAt).getTime(),
            user: session.user,
          });
          return;
        } catch {
          if (cancelled) return;
          if (attempt < RETRY_DELAYS_MS.length) {
            await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
            continue;
          }
          // alle Versuche fehlgeschlagen
          toast.error("Session expired, please login again");
          logout();
          return;
        }
      }
    }, msUntilRefresh);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.accessToken, session?.expiresAt]);
}
